import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/api/services/auth';
import BrandMark from '@/components/BrandMark';
import BrandTagline from '@/components/BrandTagline';
import BrandWord from '@/components/BrandWord';
import { mapRegistrationApiError } from '@/utils/registrationErrorMapping';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite')?.trim() || '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authService.register({ name, email, password, termsAccepted });
      navigate('/registration-confirmed', {
        replace: true,
        state: {
          termsVersion: (result as { termsVersion?: string }).termsVersion,
          termsAcceptedAt: (result as { termsAcceptedAt?: string }).termsAcceptedAt,
        },
      });
    } catch (err) {
      setError(mapRegistrationApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden />
      <div className="login-panel">
        <div className="login-brand">
          <BrandMark size="large" />
          <h1><BrandWord /></h1>
          <BrandTagline className="brand-tag login-brand-tag" />
          <p className="muted">Create your account</p>
        </div>

        <form className="login-card card card-elevated" onSubmit={onSubmit}>
          <h2>Create your account</h2>
          <p className="muted">Registration requires administrator approval.</p>
          {inviteToken ? (
            <div className="alert alert-info" role="status">
              You were invited to join an organization. Register with the invited email address, then sign in to accept
              the invitation.{' '}
              <Link to={`/login?invite=${encodeURIComponent(inviteToken)}`}>Sign in instead</Link>
            </div>
          ) : null}
          {error ? <div className="alert alert-error">{error}</div> : null}
          <label>
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              pattern="(?=.*[A-Za-z])(?=.*[0-9]).{8,}"
              title="Use at least 8 characters, including one letter and one number."
              required
            />
            <span className="muted login-field-hint">
              At least 8 characters, including one letter and one number.
            </span>
          </label>
          <label className="login-legal-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <span>
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noreferrer">
                Terms and Conditions
              </Link>{' '}
              and acknowledge the{' '}
              <Link to="/privacy" target="_blank" rel="noreferrer">
                Privacy Notice
              </Link>
              .
            </span>
          </label>
          <p className="muted login-legal-note">
            Scoutly is for business and professional use. By registering, you confirm that you are at least 18 and
            authorized to act for your organization.
          </p>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || !termsAccepted || !name || !email || !password}
          >
            {loading ? 'Submitting…' : 'Register'}
          </button>
          <p className="muted login-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
